import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Col, Row, Space, Spin, Tag, Typography } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { trpc, trpcUntypedClient } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { calculatePurchaseOrderTaxIncludedSummary } from '../utils/purchaseOrderTaxIncludedSummary';

const { Title, Text } = Typography;

interface OrderItem {
  item: {
    id: string;
    quantity: number;
    unitPrice: string;
    amount: string;
    notes?: string | null;
  };
  part: {
    id: string;
    name: string;
    displayId: string;
    partNumber?: string | null;
    unit?: string | null;
  };
}

type PrintableCompanySeal = {
  assetId: string;
  fileId: string;
  imageUrl: string;
} | null;

const formatDate = (value?: string | Date | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('ja-JP');
};

const formatMoney = (value?: string | number | null): string => {
  const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
  return `¥${Number.isFinite(numericValue) ? numericValue.toLocaleString() : '0'}`;
};

const isFormalPdfBlockedOrder = (order: unknown): boolean => {
  const typedOrder = order as { approvalRequired?: boolean | null; approvalStatus?: string | null };
  return Boolean(typedOrder?.approvalRequired) && typedOrder.approvalStatus !== 'approved';
};

export function PurchaseOrderPrintPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const didPrintRef = useRef(false);

  const { data, isLoading } = trpc.purchaseOrders.getById.useQuery(
    { id: id! },
    { enabled: !!id },
  );

  const isFormalPdfBlocked = isFormalPdfBlockedOrder(data?.order);

  const normalizedItems = useMemo<OrderItem[]>(
    () =>
      (data?.items ?? []).map((entry) => ({
        item: {
          id: entry.item?.id ?? '',
          quantity: entry.item?.quantity ?? 0,
          unitPrice: entry.item?.unitPrice ?? '0',
          amount: entry.item?.amount ?? '0',
          notes: entry.item?.notes ?? null,
        },
        part: {
          id: entry.part?.id ?? '',
          name: entry.part?.name ?? '',
          displayId: entry.part?.displayId ?? '',
          partNumber: (entry.part as { partNumber?: string | null } | undefined)?.partNumber ?? null,
          unit: entry.part?.unit ?? null,
        },
      })),
    [data?.items],
  );

  const recordPrintAndOpenDialog = useCallback(async () => {
    if (!id || isFormalPdfBlocked) return;
    await trpcUntypedClient.mutation('purchaseOrders.recordPdfOutput', {
      id,
      action: 'print',
    });
    window.print();
  }, [id, isFormalPdfBlocked]);

  useEffect(() => {
    if (isLoading || !data || didPrintRef.current) return;
    if (isFormalPdfBlocked) return;
    didPrintRef.current = true;
    const timer = window.setTimeout(() => {
      void recordPrintAndOpenDialog();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [data, isLoading, isFormalPdfBlocked, recordPrintAndOpenDialog]);

  if (isLoading || !data) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const { order, supplier } = data;
  const companyName = user?.companyName || '発注元会社';
  const contactName = user?.name || '担当者';
  const contactEmail = user?.email || '-';
  const amountSummary = calculatePurchaseOrderTaxIncludedSummary(order, normalizedItems);
  const printableCompanySeal = (data as { printableCompanySeal?: PrintableCompanySeal }).printableCompanySeal ?? null;

  return (
    <div style={{ background: '#eef2f7', minHeight: '100vh', padding: 24 }}>
      <style>
        {`
          @page { size: A4; margin: 12mm; }
          @media print {
            body { background: #fff !important; }
            .purchase-order-print-actions { display: none !important; }
            .purchase-order-print-shell { padding: 0 !important; background: #fff !important; }
            .purchase-order-print-paper { box-shadow: none !important; margin: 0 !important; width: 100% !important; min-height: auto !important; }
          }
        `}
      </style>

      <div className="purchase-order-print-actions" style={{ maxWidth: 840, margin: '0 auto 16px' }}>
        <Space wrap>
          <Button
            type="primary"
            icon={<PrinterOutlined aria-hidden="true" />}
            disabled={isFormalPdfBlocked}
            onClick={() => void recordPrintAndOpenDialog()}
          >
            印刷
          </Button>
          {isFormalPdfBlocked && <Tag color="warning">承認待ち</Tag>}
        </Space>
      </div>
      {isFormalPdfBlocked && (
        <div className="purchase-order-print-actions" style={{ maxWidth: 840, margin: '0 auto 16px' }}>
          <Alert
            type="warning"
            showIcon
            message="承認済みになるまで正式PDFを出力できません"
          />
        </div>
      )}

      <div className="purchase-order-print-shell">
        <main
          className="purchase-order-print-paper"
          data-testid="purchase-order-print-page"
          style={{
            width: 794,
            minHeight: 1123,
            margin: '0 auto',
            background: '#fff',
            padding: 48,
            color: '#0f172a',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
            boxSizing: 'border-box',
          }}
        >
          <Title level={2} style={{ textAlign: 'center', marginTop: 0 }}>
            発注書
          </Title>

          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={12}>
              <section style={{ border: '1px solid #d7dee8', padding: 14, minHeight: 96 }}>
                <Text type="secondary">発注先</Text>
                <Space direction="vertical" size={2} style={{ display: 'flex', marginTop: 8 }}>
                  <Text strong>{supplier.name} 御中</Text>
                  <Text>購買担当者様</Text>
                  <Text>FAX {(supplier as { fax?: string | null }).fax ?? '-'}</Text>
                </Space>
              </section>
            </Col>
            <Col span={12}>
              <section
                style={{
                  border: '1px solid #d7dee8',
                  padding: printableCompanySeal ? '14px 88px 14px 14px' : 14,
                  minHeight: 96,
                  position: 'relative',
                }}
              >
                <Text type="secondary">発注元</Text>
                <Space direction="vertical" size={2} style={{ display: 'flex', marginTop: 8 }}>
                  <Text>{companyName}</Text>
                  <Text>担当 {contactName}</Text>
                  <Text>{contactEmail}</Text>
                </Space>
                {printableCompanySeal && (
                  <img
                    alt="社印"
                    src={printableCompanySeal.imageUrl}
                    style={{
                      position: 'absolute',
                      top: 22,
                      right: 16,
                      width: 56,
                      height: 56,
                      objectFit: 'contain',
                      opacity: 0.96,
                    }}
                  />
                )}
              </section>
            </Col>
          </Row>

          <section style={{ border: '1px solid #d7dee8', padding: 12, marginBottom: 20 }}>
            <Space wrap size={20}>
              <Text>管理番号: {order.displayId}</Text>
              <Text>発注日: {formatDate(order.orderDate)}</Text>
              <Text>納品予定日: {formatDate(order.expectedDeliveryDate)}</Text>
            </Space>
          </section>

          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr style={{ background: '#0f4c8a', color: '#fff' }}>
              {['品名', '型式', '数量', '単位', '単価(税込)', '金額(税込)'].map((heading) => (
                  <th key={heading} style={{ textAlign: 'left', padding: 8, border: '1px solid #0f4c8a' }}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {normalizedItems.map((entry) => (
                <tr key={entry.item.id}>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>{entry.part.name}</td>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>
                    {entry.part.partNumber ?? entry.part.displayId}
                  </td>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>{entry.item.quantity}</td>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>{entry.part.unit ?? '個'}</td>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>{formatMoney(entry.item.unitPrice)}</td>
                  <td style={{ padding: 8, border: '1px solid #d7dee8' }}>{formatMoney(entry.item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Row justify="end" style={{ marginBottom: 20 }}>
            <Col span={8}>
              {[
                ['小計(税込)', amountSummary.subtotal],
                ['内消費税', amountSummary.tax],
                ['送料(税込)', amountSummary.shippingCost],
                ['税込合計', amountSummary.totalAmount],
              ].map(([label, value]) => (
                <Row key={String(label)} justify="space-between" style={{ padding: '4px 0' }}>
                  <Text strong={label === '税込合計'}>{label}</Text>
                  <Text strong={label === '税込合計'}>{formatMoney(value)}</Text>
                </Row>
              ))}
            </Col>
          </Row>

          <section style={{ border: '1px solid #d7dee8', padding: 12, minHeight: 84 }}>
            <Text strong>備考</Text>
            <div style={{ marginTop: 8 }}>{order.notes || '-'}</div>
          </section>
        </main>
      </div>
    </div>
  );
}
